import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Badge, Alert, Button, Input, Select, Card } from '@dreamtree-org/twreact-ui';
import { useAppApi } from '@/lib/api';
import Can from '@rbac/Can';
import { connectionOptionLabel } from '@/lib/connectionOptions';
import { normalizeAuditUrl } from '@/lib/auditUrl';
import DataTable from '@/components/DataTable';
import PageShell from '@/components/layout/PageShell';
import PageHeader from '@/components/layout/PageHeader';

/**
 * "UI Audits" list (`/app-ui-audit`) — rows come from routes/uiAuditRouter.js, not
 * generic CRUD: starting a run signs into a real application with stored credentials and
 * spends vision credits, so AppUiAudit's generic writes are denied by omission.
 *
 * The New-audit dialog never collects a password. It picks a `web-app` Connection, which
 * is where the encrypted credentials live — a form field here would put them in a request
 * body, and the equivalent chat tool argument would land in app_agent_traces as plaintext.
 */

export function AuditStatusBadge({ status }) {
  const variant = {
    done: 'success', running: 'primary', pending: 'secondary',
    failed: 'error', cancelled: 'secondary',
  };
  return <Badge variant={variant[status] || 'secondary'} size="sm">{status}</Badge>;
}

export function ScoreBadge({ score }) {
  if (score == null) return <span className="text-[var(--app-muted)]">—</span>;
  let variant = 'error';
  if (score >= 80) variant = 'success';
  else if (score >= 50) variant = 'warning';
  return <Badge variant={variant} size="sm">{score}/100</Badge>;
}

// Matches the clamp in routes/uiAuditRouter.js. maxLength stops the overflow in the form
// rather than letting it surface as a database error after the audit has already run.
const BRIEF_MAX_CHARS = 4000;

const STATUS_OPTIONS = [
  { label: 'All statuses', value: '' },
  { label: 'Pending', value: 'pending' },
  { label: 'Running', value: 'running' },
  { label: 'Done', value: 'done' },
  { label: 'Failed', value: 'failed' },
  { label: 'Cancelled', value: 'cancelled' },
];

const AUDIT_COLUMNS = [
  { key: 'name', label: 'Target' },
  { key: 'base_url', label: 'URL' },
  { key: 'status', label: 'Status', render: (row) => <AuditStatusBadge status={row.status} /> },
  { key: 'overall_score', label: 'Score', render: (row) => <ScoreBadge score={row.overall_score} /> },
  { key: 'pages_audited', label: 'Views' },
  { key: 'findings_count', label: 'Findings' },
  { key: 'created_at', label: 'Started', render: (row) => (row.created_at ? new Date(row.created_at).toLocaleString() : '—') },
];

/** The six fields a web-app audit target needs. Hand-written rather than rendered from
 *  the provider's dynamic field spec because that spec comes from GET
 *  /api/connections/providers, which requires `connections.view` — a third permission
 *  between an operator and a target they are already entitled to audit. */
const BLANK_TARGET = {
  name: '', base_url: '', login_path: '',
  username_selector: '', password_selector: '', submit_selector: '', success_selector: '',
  username: '', password: '',
};

/**
 * Create a web-app audit target without leaving this screen.
 *
 * Posts to /api/connections, so the username and password land AES-256-GCM encrypted in
 * app_connections.credentials_enc exactly as they would from the Connections screen —
 * this is a shortcut through the UI, never around the credential store.
 *
 * Requires connections.author AND connections.rotate (routes/connectionsRouter.js gates
 * credentials on rotate), which is why the caller wraps it in <Can action="rotate">.
 */
function NewTargetForm({ providerId, onCreated, onCancel }) {
  const { fetchApi } = useAppApi();
  const [t, setT] = useState(BLANK_TARGET);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const set = (k) => (e) => setT((prev) => ({ ...prev, [k]: e.target.value }));

  const save = async () => {
    setBusy(true);
    setErr('');
    try {
      const created = await fetchApi('connectionCreate', {
        providerId,
        name: t.name,
        config: {
          base_url: t.base_url,
          login_path: t.login_path || undefined,
          username_selector: t.username_selector || undefined,
          password_selector: t.password_selector || undefined,
          submit_selector: t.submit_selector || undefined,
          success_selector: t.success_selector || undefined,
          authorized: true,
        },
        ...(t.username || t.password
          ? { credentials: { username: t.username, password: t.password } }
          : {}),
      });
      onCreated(created.id, t.name);
    } catch (ex) {
      setErr(ex?.response?.data?.error || ex.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-2 border border-[var(--app-border)] rounded-md p-3">
      {err && <Alert variant="error" title="Could not save target">{err}</Alert>}
      <div className="grid sm:grid-cols-2 gap-2">
        <Input label="Name" value={t.name} onChange={set('name')} placeholder="Acme Staging" />
        <Input label="Base URL" value={t.base_url} onChange={set('base_url')} placeholder="https://app.acme.com" />
        <Input label="Login path" value={t.login_path} onChange={set('login_path')} placeholder="/login" />
        <Input label="Post-login selector" value={t.success_selector} onChange={set('success_selector')} placeholder='nav[aria-label="Main"]' />
        <Input label="Username / email" value={t.username} onChange={set('username')} autoComplete="off" />
        <Input label="Password" type="password" value={t.password} onChange={set('password')} autoComplete="new-password" />
      </div>
      <details className="text-sm">
        <summary className="cursor-pointer text-[var(--app-muted)]">Field selectors (optional — detected automatically if blank)</summary>
        <div className="grid sm:grid-cols-3 gap-2 pt-2">
          <Input label="Username field" value={t.username_selector} onChange={set('username_selector')} placeholder='input[name="email"]' />
          <Input label="Password field" value={t.password_selector} onChange={set('password_selector')} placeholder='input[type="password"]' />
          <Input label="Submit button" value={t.submit_selector} onChange={set('submit_selector')} placeholder='button[type="submit"]' />
        </div>
      </details>
      <p className="text-xs text-[var(--app-muted)]">
        The password is stored encrypted on the connection and is never sent to a model,
        written to an audit record, or shown in a tool trace. Only audit applications you
        are authorized to test.
      </p>
      <div className="flex gap-2">
        <Button size="sm" disabled={!t.name || !t.base_url || busy} onClick={save}>
          {busy ? 'Saving…' : 'Save target'}
        </Button>
        <Button size="sm" variant="secondary" onClick={onCancel}>Cancel</Button>
      </div>
    </div>
  );
}

/** Which AI model connection reviews the screenshots. "Auto" names the connection the
 *  worker would actually pick, rather than leaving the operator to guess. */
function VisionPicker({ connections, autoDefault, value, onChange }) {
  if (!connections.length) {
    return (
      <Alert variant="warning" title="No AI model connection">
        The measured checks will still run, but nothing can review the screenshots until an
        AI Model connection exists on the Connections screen.
      </Alert>
    );
  }
  const autoLabel = autoDefault
    ? `Auto — ${autoDefault.name} (${autoDefault.provider})`
    : 'Auto';
  return (
    <div className="space-y-1">
      <label className="block text-sm font-medium" htmlFor="ui-audit-vision">Reviewed by</label>
      <Select
        id="ui-audit-vision"
        options={[{ label: autoLabel, value: '' }, ...connections.map((c) => ({
          label: connectionOptionLabel(c), value: String(c.id),
        }))]}
        value={value}
        onChange={onChange}
        placeholder={autoLabel}
      />
    </div>
  );
}

/**
 * A typed-in URL — the first thing on the form and the common case.
 *
 * This used to sit behind a "A public URL" tab, which is how an operator ended up staring
 * at a dropdown of targets they had not configured yet with no way to just name a site.
 * A two-option form does not need a tab strip.
 */
function UrlField({ url, setUrl, disabled }) {
  return (
    <div className="space-y-1">
      <Input
        label="URL"
        value={url}
        onChange={(e) => setUrl(e.target.value)}
        placeholder="https://example.com"
        disabled={disabled}
      />
      <p className="text-xs text-[var(--app-muted)]">
        {disabled
          ? 'Clear the saved target below to audit a URL instead.'
          : 'Audits any public site. Nothing is signed into and nothing is stored. Private and localhost addresses need a saved target that allows them.'}
      </p>
    </div>
  );
}

function SavedTargetPanel({
  targets, providerId, connectionId, setConnectionId,
  addingTarget, setAddingTarget, onTargetCreated, disabled,
}) {
  return (
    <div className="space-y-2">
      <div className="space-y-1">
        <label className="block text-sm font-medium" htmlFor="ui-audit-target">Saved target</label>
        <Select
          id="ui-audit-target"
          options={targets.map((t) => ({
            label: `${t.name}${t.requiresLogin ? '' : ' (no sign-in)'}`,
            value: String(t.id),
          }))}
          value={connectionId}
          onChange={setConnectionId}
          placeholder={targets.length ? 'Pick a saved target' : 'No saved targets yet'}
          disabled={disabled}
        />
        <p className="text-xs text-[var(--app-muted)]">
          {disabled
            ? 'Clear the URL above to use a saved target instead.'
            : 'For a site that needs signing in. The credentials live encrypted on the target, so they are never typed here.'}
        </p>
      </div>
      {addingTarget ? (
        <NewTargetForm
          providerId={providerId}
          onCreated={(id) => { setAddingTarget(false); onTargetCreated(id); }}
          onCancel={() => setAddingTarget(false)}
        />
      ) : (
        <Can
          module="connections"
          action="rotate"
          fallback={(
            <p className="text-xs text-[var(--app-muted)]">
              Saved targets are added on the Connections screen (“Web App (audit target)”) —
              that is where the encrypted sign-in credentials live.
            </p>
          )}
        >
          <Button size="sm" variant="secondary" disabled={!providerId} onClick={() => setAddingTarget(true)}>
            + New target
          </Button>
        </Can>
      )}
    </div>
  );
}

/**
 * Start a run against either a public URL or a saved target.
 *
 * One flat form, not two tabs. The two inputs are genuinely different things — a URL needs
 * no setup and stores nothing, while a site behind a login needs somewhere encrypted to
 * keep the credentials — but they are two fields, not two screens, and putting the URL
 * behind a tab is what left an operator staring at an empty target dropdown with no
 * apparent way to just name a site.
 */
function NewAuditDialog({ onClose, onCreated }) {
  const { fetchApi } = useAppApi();
  const [targets, setTargets] = useState([]);
  const [providerId, setProviderId] = useState(null);
  const [visionConnections, setVisionConnections] = useState([]);
  const [autoDefault, setAutoDefault] = useState(null);
  const [addingTarget, setAddingTarget] = useState(false);
  const [url, setUrl] = useState('');
  const [connectionId, setConnectionId] = useState('');
  const [visionConnectionId, setVisionConnectionId] = useState('');
  const [flowContext, setFlowContext] = useState('');
  const [maxPages, setMaxPages] = useState('8');
  const [visionEnabled, setVisionEnabled] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const loadTargets = () => fetchApi('uiAuditTargets')
    .then((res) => { setTargets(res?.targets || []); setProviderId(res?.webAppProviderId || null); })
    .catch((ex) => setErr(ex?.response?.data?.error || ex.message));

  useEffect(() => {
    loadTargets();
    fetchApi('uiAuditVisionConnections')
      .then((res) => { setVisionConnections(res?.connections || []); setAutoDefault(res?.autoDefault || null); })
      .catch(() => { /* the picker degrades to "no connection"; the run still measures */ });
  }, []);

  const submit = async () => {
    setBusy(true);
    setErr('');
    try {
      await fetchApi('uiAuditCreate', {
        // Exactly one, matching the server rule: resolveBaseUrl (routes/uiAuditRouter.js)
        // rejects both-at-once and neither. The form disables whichever is not in use, so
        // this branch can never send both.
        ...(url.trim() ? { baseUrl: normalizeAuditUrl(url) } : { connectionId: Number(connectionId) }),
        scope: flowContext ? { flowContext } : null,
        maxPages: Number(maxPages) || 8,
        visionEnabled,
        visionConnectionId: visionConnectionId ? Number(visionConnectionId) : null,
      });
      onCreated();
    } catch (ex) {
      setErr(ex?.response?.data?.error || ex.message);
    } finally {
      setBusy(false);
    }
  };

  const canStart = !!url.trim() || !!connectionId;

  return (
    <Card className="p-4 space-y-3">
      <h3 className="font-semibold">New audit</h3>
      {err && <Alert variant="error" title="Could not start">{err}</Alert>}

      <UrlField url={url} setUrl={setUrl} disabled={!!connectionId} />

      <div className="flex items-center gap-3 text-xs text-[var(--app-muted)]">
        <span className="h-px flex-1 bg-[var(--app-border)]" />
        or audit a site that needs signing in
        <span className="h-px flex-1 bg-[var(--app-border)]" />
      </div>

      <SavedTargetPanel
        targets={targets}
        providerId={providerId}
        connectionId={connectionId}
        setConnectionId={setConnectionId}
        addingTarget={addingTarget}
        setAddingTarget={setAddingTarget}
        onTargetCreated={(id) => { setConnectionId(String(id)); loadTargets(); }}
        disabled={!!url.trim()}
      />

      <div className="space-y-1">
        <label className="block text-sm font-medium" htmlFor="ui-audit-brief">Audit brief (optional)</label>
        <textarea
          id="ui-audit-brief"
          className="w-full min-h-[90px] rounded-md border border-[var(--app-border)] bg-[var(--app-surface)] p-2 text-sm"
          value={flowContext}
          maxLength={BRIEF_MAX_CHARS}
          onChange={(e) => setFlowContext(e.target.value)}
          placeholder={'e.g. new-customer onboarding.\n\nOr a fuller brief: what these screens are for, who uses them, and what the review should pay closest attention to.'}
        />
        <div className="flex justify-between gap-2">
          <p className="text-xs text-[var(--app-muted)]">
            Given to the visual review, so it judges the screens against the job the user is
            actually doing. A short phrase or several paragraphs both work.
          </p>
          <span className="text-xs text-[var(--app-muted)] shrink-0 tabular-nums">
            {flowContext.length} / {BRIEF_MAX_CHARS}
          </span>
        </div>
      </div>

      <div className="flex gap-4 items-end flex-wrap">
        <div className="space-y-1 w-32">
          <label className="block text-sm font-medium" htmlFor="ui-audit-pages">Max views</label>
          <Input id="ui-audit-pages" type="number" min="1" max="50" value={maxPages} onChange={(e) => setMaxPages(e.target.value)} />
        </div>
        <label className="flex items-center gap-2 text-sm pb-2">
          <input type="checkbox" checked={visionEnabled} onChange={(e) => setVisionEnabled(e.target.checked)} />
          Visual review
        </label>
      </div>

      {visionEnabled && (
        <VisionPicker
          connections={visionConnections}
          autoDefault={autoDefault}
          value={visionConnectionId}
          onChange={setVisionConnectionId}
        />
      )}

      <p className="text-xs text-[var(--app-muted)]">
        Each view is captured at desktop and mobile widths. Screenshots and the report are
        stored privately and served only to signed-in members of this company.
      </p>
      <div className="flex gap-2">
        <Button size="sm" disabled={!canStart || busy} onClick={submit}>
          {busy ? 'Starting…' : 'Start audit'}
        </Button>
        <Button size="sm" variant="secondary" onClick={onClose}>Cancel</Button>
      </div>
    </Card>
  );
}

export default function UiAuditsPage() {
  const { fetchApi } = useAppApi();
  const nav = useNavigate();
  const [audits, setAudits] = useState([]);
  const [status, setStatus] = useState('');
  const [showNew, setShowNew] = useState(false);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  const load = () => {
    fetchApi('uiAuditList')
      .then((res) => setAudits(res?.audits || []))
      .catch((ex) => setErr(ex?.response?.data?.error || ex.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    setLoading(true);
    load();
    // A run takes minutes and advances through pending → running → done in the worker,
    // with no push channel — the same 10s poll the calls list uses for the same reason.
    const timer = setInterval(load, 10000);
    return () => clearInterval(timer);
  }, []);

  const rows = status ? audits.filter((a) => a.status === status) : audits;

  return (
    <PageShell className="space-y-3">
      <PageHeader
        title="UI Audits"
        subtitle={<>Headless-browser UI/UX and accessibility audits — measured checks plus a visual review, reported by severity.</>}
        actions={(
          <Can module="uiaudits" action="author">
            <Button size="sm" onClick={() => setShowNew((v) => !v)}>New audit</Button>
          </Can>
        )}
      />

      {err && <Alert variant="error" title="Could not load">{err}</Alert>}
      {showNew && <NewAuditDialog onClose={() => setShowNew(false)} onCreated={() => { setShowNew(false); load(); }} />}

      <DataTable
        data={rows}
        columns={AUDIT_COLUMNS}
        loading={loading}
        emptyText='No audits yet — start one with "New audit".'
        search={{ placeholder: 'Search audits…', keys: ['name', 'base_url'] }}
        toolbar={(
          <div className="w-48">
            <Select options={STATUS_OPTIONS} value={status} onChange={setStatus} placeholder="All statuses" />
          </div>
        )}
        onRowClick={(audit) => nav(`/app-ui-audit/${audit.id}`)}
        onReload={load}
      />
    </PageShell>
  );
}
